import { useEffect, useState } from 'react';
import { useRevalidator, useRouteLoaderData } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg, type SessionResponse } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { CampaignUnsubscribes } from './campaign-unsubscribes';
import { RecipientSummary, type RecipientPreview } from './recipient-summary';
import { SegmentPicker, type Segment } from './campaign-form';

export interface CampaignData {
  id: string;
  name: string;
  status: string;
  templateId: string;
  templateVersion: number;
  segmentIds: string[];
  excludeSegmentIds: string[];
  fromName: string;
  fromAddress: string;
  replyTo: string | null;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  testSentAt: string | null;
  recipientCount: number;
  stats: {
    recipients?: number;
    sent?: number;
    delivered?: number;
    opened?: number;
    clicked?: number;
    uniqueOpens?: number;
    uniqueClicks?: number;
    unsubscribeClicks?: number;
    uniqueUnsubscribeClicks?: number;
    unsubscribed?: number;
    bounced?: number;
    complained?: number;
    failed?: number;
    openRate?: number | null;
    clickRate?: number | null;
    links?: Array<{ linkId: string; url: string | null; clicks: number }>;
    held?: string;
    error?: string;
  } | null;
}

export function CampaignActions({
  campaign,
  segments,
  when,
  onWhenChange,
}: {
  campaign: CampaignData;
  segments: Segment[];
  when: string;
  onWhenChange: (when: string) => void;
}) {
  const { revalidate } = useRevalidator();
  const session = useRouteLoaderData<SessionResponse>('app');
  const [audience, setAudience] = useState({
    segmentIds: campaign.segmentIds,
    excludeSegmentIds: campaign.excludeSegmentIds,
  });
  const [recipients, setRecipients] = useState<RecipientPreview | null>(null);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [testTo, setTestTo] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmBig, setConfirmBig] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const editable = ['draft', 'test_sent'].includes(campaign.status);
  const isAdmin = session?.user.role === 'admin';

  useEffect(() => {
    let active = true;
    api<RecipientPreview>(`/campaigns/${campaign.id}/recipients`)
      .then((result) => {
        if (active) setRecipients(result);
      })
      .catch((error) => {
        if (active) setRecipientError(errMsg(error, 'Could not check recipients. Try again.'));
      });
    return () => {
      active = false;
    };
  }, [campaign.id, refresh]);

  function refreshRecipients() {
    setRecipients(null);
    setRecipientError(null);
    setConfirmBig(false);
    setRefresh((value) => value + 1);
  }

  const blockedReason = !editable
    ? campaign.status === 'scheduled'
      ? 'This campaign is already scheduled.'
      : campaign.status === 'sending'
        ? 'This campaign is currently sending.'
        : `This campaign is ${campaign.status}. Create a new campaign to send it again.`
    : !isAdmin
      ? 'An admin must schedule or send this campaign.'
      : !campaign.segmentIds.length
        ? 'Select at least one segment.'
        : recipientError
          ? 'Recipient checks failed. Retry before sending.'
          : !recipients
            ? 'Checking recipients and sending restrictions…'
            : recipients.blockingReasons?.length
              ? 'Resolve the sending restrictions shown above.'
              : recipients.count === 0
                ? 'No eligible recipients. Review the recipient summary before scheduling.'
                : null;

  async function run(name: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(name);
    setMsg(null);
    try {
      await fn();
      setMsg({ kind: 'ok', text: ok });
      await revalidate();
    } catch (err) {
      setMsg({ kind: 'error', text: errMsg(err, 'Failed') });
      if (name === 'audience')
        setAudience({
          segmentIds: campaign.segmentIds,
          excludeSegmentIds: campaign.excludeSegmentIds,
        });
      if (name === 'schedule' || name === 'audience') refreshRecipients();
    } finally {
      setBusy(null);
    }
  }

  function toggleAudience(key: 'segmentIds' | 'excludeSegmentIds', id: string) {
    if (busy) return;
    const selected = audience[key];
    const next = selected.includes(id)
      ? selected.filter((value) => value !== id)
      : [...selected, id];
    setConfirmBig(false);
    setAudience((current) => ({ ...current, [key]: next }));
    setRecipients(null);
    setRecipientError(null);
    void run(
      'audience',
      () =>
        api(`/campaigns/${campaign.id}`, {
          method: 'PATCH',
          json: { [key]: next },
        }),
      'Audience saved',
    );
  }

  const schedule = () => {
    if (busy !== null || blockedReason || !recipients) return;
    if (
      when &&
      (!Number.isFinite(new Date(when).getTime()) || new Date(when).getTime() <= Date.now())
    ) {
      setMsg({ kind: 'error', text: 'Choose a future date and time, or clear it to send now.' });
      return;
    }
    if (recipients.count > 1000 && !confirmBig) {
      setConfirmBig(true);
      setMsg({
        kind: 'error',
        text: `${recipients.count} recipients — click again to confirm a send this size.`,
      });
      return;
    }
    void run(
      'schedule',
      () =>
        api(`/campaigns/${campaign.id}/schedule`, {
          method: 'POST',
          json: {
            scheduledAt: when ? new Date(when).toISOString() : undefined,
            confirmedCount: recipients.count,
          },
        }),
      when ? 'Scheduled' : 'Queued — the dispatcher sends within a minute',
    );
  };

  const s = campaign.stats;
  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <section className="min-w-0 space-y-5 rounded-lg border bg-white p-5 text-sm">
        <h2 className="text-lg">Send campaign</h2>
        {editable && (
          <fieldset disabled={busy !== null} className="min-w-0 space-y-4">
            <SegmentPicker
              segments={segments}
              selected={audience.segmentIds}
              title="Send to (union of segments)"
              onToggle={(id) => toggleAudience('segmentIds', id)}
            />
            <details>
              <summary className="cursor-pointer text-muted-foreground">
                Exclude segments ({audience.excludeSegmentIds.length})
              </summary>
              <div className="mt-3">
                <SegmentPicker
                  segments={segments}
                  selected={audience.excludeSegmentIds}
                  title="Exclude"
                  onToggle={(id) => toggleAudience('excludeSegmentIds', id)}
                />
              </div>
            </details>
            <p className="text-xs text-muted-foreground">
              {busy === 'audience'
                ? 'Saving audience…'
                : 'Audience changes save automatically. Counts include unsubscribes and exclusions.'}
            </p>
          </fieldset>
        )}
        {recipients ? (
          <RecipientSummary recipients={recipients} />
        ) : recipientError ? (
          <p role="alert" className="rounded bg-red-50 p-3 text-red-800">
            {recipientError}
          </p>
        ) : (
          <p role="status" className="text-muted-foreground">
            Checking recipients and sending restrictions…
          </p>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null || (!recipients && !recipientError)}
          onClick={refreshRecipients}
        >
          {recipientError ? 'Retry recipient check' : 'Refresh recipients'}
        </Button>
        <div className="space-y-2">
          <label htmlFor="campaign-schedule-at" className="block font-medium">
            Schedule or send
          </label>
          <p className="text-muted-foreground">
            Choose a date and time ({Intl.DateTimeFormat().resolvedOptions().timeZone}), or leave it
            empty to send now.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="campaign-schedule-at"
              type="datetime-local"
              value={when}
              onChange={(e) => {
                onWhenChange(e.target.value);
                setConfirmBig(false);
              }}
              className="h-9 min-w-0 max-w-full rounded-md border px-2"
            />
            <Button
              size="sm"
              aria-describedby={blockedReason ? 'campaign-schedule-reason' : undefined}
              disabled={busy !== null || blockedReason !== null}
              onClick={schedule}
            >
              {busy === 'schedule' ? 'Scheduling…' : when ? 'Schedule' : 'Send now'}
            </Button>
            {['scheduled', 'sending'].includes(campaign.status) && (
              <Button
                size="sm"
                variant="destructive"
                disabled={busy !== null || !isAdmin}
                onClick={() =>
                  run(
                    'cancel',
                    () => api(`/campaigns/${campaign.id}/cancel`, { method: 'POST' }),
                    'Canceled',
                  )
                }
              >
                Cancel
              </Button>
            )}
          </div>
          {blockedReason && (
            <p id="campaign-schedule-reason" className="text-muted-foreground">
              {blockedReason}
            </p>
          )}
        </div>
        <details className="space-y-3 border-t pt-4">
          <summary className="cursor-pointer font-medium">
            Preview and test email (optional)
          </summary>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() =>
              run(
                'preview',
                () =>
                  api<{ html: string }>(`/templates/${campaign.templateId}/preview`, {
                    method: 'POST',
                    json: { version: campaign.templateVersion },
                  }).then((r) => setPreview(r.html)),
                'Preview rendered',
              )
            }
          >
            {busy === 'preview' ? 'Rendering…' : 'Render preview'}
          </Button>
          <label htmlFor="campaign-test-to" className="block font-medium">
            Send a test email
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              id="campaign-test-to"
              type="email"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              className="h-9 min-w-0 flex-1 rounded-md border px-3"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null || !testTo.trim()}
              onClick={() => {
                if (!confirm(`Send a test of this campaign to ${testTo}?`)) return;
                void run(
                  'test',
                  () =>
                    api(`/campaigns/${campaign.id}/test-send`, {
                      method: 'POST',
                      json: { to: testTo },
                    }),
                  'Test email sent',
                );
              }}
            >
              {busy === 'test' ? 'Sending test…' : 'Send test'}
            </Button>
          </div>
          <p className="text-muted-foreground">
            {campaign.testSentAt
              ? `Last test sent: ${fmtDateTime(campaign.testSentAt)}`
              : 'Preview and test emails are optional. You can send a test without rendering a preview.'}
          </p>
          {preview && (
            <iframe
              title="preview"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={preview}
              className="h-96 w-full rounded border bg-white"
            />
          )}
        </details>
        {campaign.scheduledAt && (
          <p className="text-xs text-muted-foreground">
            Scheduled for {fmtDateTime(campaign.scheduledAt)}
            {campaign.startedAt ? ` · started ${fmtDateTime(campaign.startedAt)}` : ''}
            {campaign.completedAt ? ` · completed ${fmtDateTime(campaign.completedAt)}` : ''}
          </p>
        )}
        {s?.held && (
          <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">
            Waiting for the daily sending limit to allow this campaign. It will retry automatically.
          </p>
        )}
        {s?.error && <p className="rounded bg-red-50 p-2 text-xs text-red-800">{s.error}</p>}
        {msg && (
          <p
            role="status"
            className={`rounded-md p-2 text-xs ${msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}
          >
            {msg.text}
          </p>
        )}
      </section>

      <section className="min-w-0 space-y-3 rounded-lg border bg-white p-5 text-sm">
        <h2 className="text-lg">Results</h2>
        {!s || s.sent === undefined ? (
          <p className="text-muted-foreground">Stats appear once the campaign is sending.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 2xl:grid-cols-4">
              {[
                ['Recipients', s.recipients],
                ['Sent', s.sent],
                ['Delivered', s.delivered],
                [
                  'Unique opens',
                  `${s.uniqueOpens ?? 0}${s.openRate != null ? ` (${s.openRate}%)` : ''}`,
                ],
                [
                  'Unique content clicks',
                  `${s.uniqueClicks ?? 0}${s.clickRate != null ? ` (${s.clickRate}%)` : ''}`,
                ],
                ['Unsubscribed', s.unsubscribed],
                ['Bounced', s.bounced],
                ['Complained', s.complained],
                ['Failed', s.failed],
              ].map(([label, value]) =>
                label === 'Unsubscribed' ? (
                  <CampaignUnsubscribes
                    key="unsubscribed"
                    campaignId={campaign.id}
                    count={s.unsubscribed ?? 0}
                  />
                ) : (
                  <div key={String(label)} className="min-w-0 rounded-md border p-2">
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="text-lg">{value ?? 0}</div>
                  </div>
                ),
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Content clicks exclude unsubscribe links. Visits may include automated email checks.
            </p>
            <div className="rounded-md border p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span>Unsubscribe link visits</span>
                <span className="tabular-nums">
                  {s.unsubscribeClicks ?? 0} visits · {s.uniqueUnsubscribeClicks ?? 0} recipients
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Opening this page does not unsubscribe a recipient; they must confirm.
              </p>
            </div>
            {s.links && s.links.length > 0 && (
              <div className="min-w-0">
                <table
                  className="data-table w-full table-fixed"
                  aria-label="Content clicks per link"
                >
                  <caption className="mb-1 text-left text-xs text-muted-foreground">
                    Content clicks per link
                  </caption>
                  <colgroup>
                    <col />
                    <col className="w-16" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Destination</th>
                      <th scope="col" className="text-right">
                        Clicks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.links.map((l) => (
                      <tr key={l.linkId}>
                        <td className="whitespace-normal text-xs [overflow-wrap:anywhere]">
                          {/^\d+$/.test(l.linkId) && (
                            <span className="mr-2 text-muted-foreground">#{l.linkId}</span>
                          )}
                          {l.url ?? 'Unknown destination'}
                        </td>
                        <td className="text-right tabular-nums">{l.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
